import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Flame, ShieldCheck } from "lucide-react";
import { API } from "@/lib/auth";
import { Skel } from "@/components/market/Shared";
import { RiskMeter } from "./Risk";

const Row = ({ r, src, onOpen }) => {
  const body = <>
    <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{r.label}</div>
      <div className="flex items-center gap-2 mt-1"><RiskMeter level={r.risk} compact /><span className="text-[11px] text-slate-500 font-num">ends {new Date(r.ends).toLocaleDateString([], { month: "short", day: "numeric" })}</span></div></div>
    <div className="text-right shrink-0"><div className={`font-num font-extrabold ${r.risk >= 4 ? "text-rose-300" : "text-emerald-300"}`}>{r.side_name.length > 16 ? r.side === "yes" ? "Yes" : "No" : r.side_name} {r.chance_pct}¢</div>
      <div className="text-[11px] text-slate-500 font-num">pays {r.payout_x}x · BE {r.breakeven_pct}%</div></div>
    {src === "kalshi" && <ExternalLink className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
  </>;
  const cls = "w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.03] transition-colors";
  return src === "kalshi" ? <a href={r.url} target="_blank" rel="noreferrer" className={cls} data-testid={`zone-row-${r.id}`}>{body}</a> :
    <button onClick={() => onOpen(r.id)} className={cls} data-testid={`zone-row-${r.id}`}>{body}</button>;
};

export const Zones = ({ onOpen }) => {
  const [zone, setZone] = useState("safe");
  const [src, setSrc] = useState("polymarket");
  const [d, setD] = useState(null);
  useEffect(() => {
    const get = () => axios.get(`${API}/zones`, { params: { source: src } }).then((r) => setD(r.data)).catch(() => setD((x) => x || { error: true }));
    setD(null); get();
    const iv = setInterval(get, 60000);
    return () => clearInterval(iv);
  }, [src]);
  const safe = zone === "safe";
  return (
    <section className={`glass overflow-hidden ${safe ? "" : "ring-1 ring-rose-500/30"}`} data-testid="zones">
      <div className="p-4 space-y-3 border-b border-[var(--line)]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-[var(--line)] p-1 text-xs font-extrabold uppercase tracking-wide">
            <button onClick={() => setZone("safe")} className={`px-4 py-2 rounded-lg flex items-center gap-1.5 ${safe ? "bg-emerald-500 text-[#03140D]" : "text-slate-400"}`} data-testid="zone-safe-tab"><ShieldCheck className="w-4 h-4" />Safe zone</button>
            <button onClick={() => setZone("risk")} className={`px-4 py-2 rounded-lg flex items-center gap-1.5 ${!safe ? "bg-rose-500 text-white" : "text-slate-400"}`} data-testid="zone-risk-tab"><Flame className="w-4 h-4" />Risk zone</button>
          </div>
          <div className="flex gap-1 text-[11px] font-bold">{[["polymarket", "Polymarket US"], ["kalshi", "Kalshi"]].map(([id, n]) => (
            <button key={id} onClick={() => setSrc(id)} className={`px-3 py-1.5 rounded-full border ${src === id ? "border-[var(--cyan)] text-[var(--cyan)]" : "border-[var(--line)] text-slate-500"}`} data-testid={`zone-src-${id}`}>{n}</button>))}</div>
        </div>
        <p className="text-xs text-slate-400" data-testid="zone-explain">{d?.explain?.[safe ? "safe" : "risk"]}
          {!safe && " Every loss in your history comes with a \"Why did I lose?\" breakdown."}</p>
      </div>
      {!d ? <div className="p-4"><Skel h="h-40" /></div> : d.error ? <p className="p-4 text-sm text-slate-400">Markets unavailable right now.</p> :
        <div className="divide-y divide-[var(--line)] max-h-[440px] overflow-y-auto">{(safe ? d.safe : d.risk).map((r) => <Row key={r.id} r={r} src={src} onOpen={onOpen} />)}
          {(safe ? d.safe : d.risk).length === 0 && <p className="p-4 text-sm text-slate-400">Nothing qualifies right now.</p>}</div>}
    </section>
  );
};
